import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import AddressForm, {
  getAddressError,
  isAddressValid,
  useAddressState,
} from '@/components/AddressForm/AddressForm';
import { InputLocale } from '@/components/InputLocale/InputLocale';
import InputPhone, { isPhoneValid, usePhoneState } from '@/components/InputPhone/InputPhone';
import CustomFieldsGroup from '@/components/Store/CustomFieldsGroup';
import Button from '@/design_system/Button';
import InputNumber from '@/design_system/InputNumber';
import InputText from '@/design_system/InputText/InputText';
import { Label } from '@/design_system/Label/Label';
import Message from '@/design_system/Message';
import Stack from '@/design_system/Stack';
import TextArea from '@/design_system/TextArea';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconAdd from '@/icons/Add.svg';
import IconTrash from '@/icons/Trash.svg';
import { isAddressEmpty } from '@/models/address';
import { CustomFieldsType } from '@/models/organization';
import { Store } from '@/models/store';
import { hasPermission } from '@/models/user';
import { useCurrentOrganization, useCurrentSession, useGeoIpCountry } from '@/services/auth';

import { GroupsSelector } from '../GroupsSelector/GroupsSelector';

export const StoreForm = ({
  state,
  error,
  isDisabled = false,
  size = 'medium',
  variant = 'default',
  allowExternalIdEdition = false,
  allowSalesAdvisorsEdition = false,
}: {
  state: StoreState;
  error?: string;
  isDisabled?: boolean;
  size?: 'medium' | 'large';
  variant?: 'default' | 'brand';
  allowExternalIdEdition?: boolean;
  allowSalesAdvisorsEdition?: boolean;
}) => {
  const { t } = useLingui();
  const {
    name,
    setName,
    groups,
    setGroups,
    address,
    setAddress,
    logisticsAddress,
    setLogisticsAddress,
    phone,
    setPhone,
    externalId,
    setExternalId,
    openingHours,
    setOpeningHours,
    customFields,
    setCustomFields,
    defaultLocale,
    setDefaultLocale,
    salesAdvisors,
    setSalesAdvisors,
    currency,
    setCurrency,
    margin,
    setMargin,
    taxRates,
    setTaxRates,
  } = state;

  const [organization] = useCurrentOrganization();
  const { currentSession } = useCurrentSession();

  const { nameError, addressError, logisticsAddressError, phoneError, salesAdvisorError } =
    getStoreError({
      state,
      allowSalesAdvisorsEdition,
    });

  const canUpdateGroups = hasPermission(currentSession, 'manage_entities', {
    organizationId: organization?.id,
  });

  const allowCurrencyConversionParamEdition = organization?.config.currencyConversion?.enabled;

  return (
    <Stack gap="24px">
      <InputText
        label={<Trans id="components.store-form.field.name.label">Name of the store</Trans>}
        type="text"
        placeholder={t({
          id: 'components.store-form.field.name.placeholder',
          message: 'Name of the store',
        })}
        size={size}
        value={name}
        onChange={setName}
        isDisabled={isDisabled}
        error={
          state.showErrors && nameError
            ? t({
                id: 'components.store-form.field.name.error',
                message: 'Please add a name',
              })
            : undefined
        }
      />

      {canUpdateGroups && <GroupsSelector groups={groups} setGroups={setGroups} />}

      <InputPhone
        label={<Trans id="components.store-form.field.phone.label">Phone</Trans>}
        value={phone}
        onChange={setPhone}
        defaultCountry={address?.country}
        size={size}
        isDisabled={isDisabled}
        variant={variant}
        isInvalid={state.showErrors && phoneError}
      />

      <AddressForm
        label={<Trans id="components.store-form.field.address.label">Main address</Trans>}
        value={address}
        onChange={setAddress}
        isDisabled={isDisabled}
        contact={true}
        size={size}
        variant={variant}
        error={state.showErrors && addressError ? getAddressError(address, true) : undefined}
      />

      <AddressForm
        label={
          <Trans id="components.store-form.field.logistics-address.label">
            Logistics address (optional)
          </Trans>
        }
        value={logisticsAddress}
        onChange={setLogisticsAddress}
        isDisabled={isDisabled}
        contact={false}
        size={size}
        variant={variant}
        error={
          state.showErrors && logisticsAddressError
            ? getAddressError(logisticsAddress, false)
            : undefined
        }
      />

      {allowExternalIdEdition && (
        <InputText
          label={<Trans id="components.store-form.field.external-id.label">External ID</Trans>}
          value={externalId}
          onChange={setExternalId}
          size={size}
          isDisabled={isDisabled}
        />
      )}

      <TextArea
        label={<Trans id="components.store-form.field.opening-hours.label">Opening hours</Trans>}
        placeholder={t({
          id: 'components.store-form.field.opening-hours.placeholder',
          message: 'E.g: Monday to Friday 9am - 5pm',
        })}
        value={openingHours}
        onChange={(e) => setOpeningHours(e.target.value)}
        size={size}
        disabled={isDisabled}
      />

      <InputLocale
        value={defaultLocale}
        onChange={setDefaultLocale}
        size={size}
        isDisabled={isDisabled}
      />

      {allowSalesAdvisorsEdition && (
        <SalesAdvisors
          value={salesAdvisors}
          onChange={setSalesAdvisors}
          isInvalid={state.showErrors && salesAdvisorError}
        />
      )}

      {allowCurrencyConversionParamEdition && (
        <>
          <InputText
            label={t({ id: 'components.store-form.field.currency', message: 'Currency' })}
            placeholder={t({ id: 'components.store-form.field.currency.EUR', message: 'EUR' })}
            value={currency}
            onChange={(value) => setCurrency(value as typeof currency)}
          />
          <InputNumber
            label={t({ id: 'components.store-form.field.margin', message: 'Margin' })}
            value={margin ?? 0}
            onChange={setMargin}
          />
          <InputNumber
            label={t({ id: 'components.store-form.field.taxRates', message: 'Tax rates' })}
            value={taxRates[0]}
            onChange={(tax) => setTaxRates([tax])}
          />
        </>
      )}

      {organization?.config?.customFields?.store && (
        <CustomFieldsGroup
          fields={organization?.config?.customFields?.store ?? []}
          customFields={customFields}
          setCustomFields={setCustomFields}
          size={size}
          disabled={isDisabled}
        />
      )}

      {error && (
        <Paragraph weight="medium" color="danger" align="center">
          {error}
        </Paragraph>
      )}
    </Stack>
  );
};

const SalesAdvisors = ({
  value,
  onChange,
  isInvalid,
}: {
  value: string[];
  onChange: (value: string[]) => void;
  isInvalid?: boolean;
}) => {
  const { t } = useLingui();

  return (
    <Label
      label={t({
        id: 'components.store-form.field.sales-advisor.label',
        message: 'Sales advisors',
      })}
    >
      <Stack gap="0.5rem">
        {value.map((item, index) => (
          <Stack key={index} row alignItems="center" gap="0.5rem">
            <InputText
              aria-label={t({
                id: 'components.store-form.field.sales-advisor.placeholder',
                message: "Sales advisor's name",
              })}
              placeholder={t({
                id: 'components.store-form.field.sales-advisor.placeholder',
                message: "Sales advisor's name",
              })}
              value={item}
              onChange={(newItem) =>
                onChange(value.map((oldItem, _index) => (index === _index ? newItem : oldItem)))
              }
              style={{ flex: 1 }}
              isInvalid={isInvalid && !item}
            />
            <Button
              iconOnly
              tooltip={t({
                id: 'components.store-form.field.sales-advisor.delete',
                message: `Remove ${item}`,
              })}
              ariaLabel={t({
                id: 'components.store-form.field.sales-advisor.delete',
                message: `Remove ${item}`,
              })}
              onPress={() => onChange(value.filter((_, _index) => index !== _index))}
              variant="secondary"
              size="large"
            >
              <IconTrash />
            </Button>
          </Stack>
        ))}
        {isInvalid && (
          <Message type="error">
            <Trans id="components.store-form.field.sales-advisor.error">
              Please add a name for each sales advisor
            </Trans>
          </Message>
        )}
        <Button
          variant="secondary"
          onPress={() => onChange([...value, ''])}
          style={{ alignSelf: 'flex-start' }}
        >
          <IconAdd />
          <Trans id="components.store-form.field.sales-advisor.add">Add a sales advisor</Trans>
        </Button>
      </Stack>
    </Label>
  );
};

export const useStoreState = (initialState?: Partial<Store> | null) => {
  const [name, setName] = useState(initialState?.name ?? '');
  const [groups, setGroups] = useState(initialState?.groups ?? []);
  const geoIpCountry = useGeoIpCountry();
  const [address, setAddress] = useAddressState(initialState?.address ?? { country: geoIpCountry });
  const [logisticsAddress, setLogisticsAddress] = useAddressState(
    initialState?.logisticsAddress ?? null
  );
  const [phone, setPhone] = usePhoneState(
    initialState?.contactInfo?.phone ?? null,
    initialState?.address?.country ?? geoIpCountry
  );

  const [customFields, setCustomFields] = useState<CustomFieldsType>(
    initialState?.organizationConfig?.customFields ?? {}
  );

  const [external, setExternal] = useState(initialState?.organizationConfig?.external ?? false);
  const [externalId, setExternalId] = useState(initialState?.organizationConfig?.externalId ?? '');
  const [openingHours, setOpeningHours] = useState(initialState?.storeConfig?.openingHours ?? '');

  const [defaultLocale, setDefaultLocale] = useState(initialState?.defaultLocale ?? 'en');
  const [salesAdvisors, setSalesAdvisors] = useState(
    initialState?.storeConfig?.salesAdvisors ?? []
  );
  const [currency, setCurrency] = useState(initialState?.currency ?? 'EUR');
  const [margin, setMargin] = useState(initialState?.margin);
  const [taxRates, setTaxRates] = useState(initialState?.taxRates ?? []);

  const [showErrors, setShowErrors] = useState(false);

  return {
    name,
    setName,
    groups,
    setGroups,
    address,
    setAddress,
    logisticsAddress,
    setLogisticsAddress,
    phone,
    setPhone,
    external,
    setExternal,
    externalId,
    setExternalId,
    openingHours,
    setOpeningHours,
    customFields,
    setCustomFields,
    defaultLocale,
    setDefaultLocale,
    salesAdvisors,
    setSalesAdvisors,
    showErrors,
    setShowErrors,
    currency,
    setCurrency,
    margin,
    setMargin,
    taxRates,
    setTaxRates,
  };
};

type StoreState = ReturnType<typeof useStoreState>;

export const getStoreError = ({
  state,
  allowSalesAdvisorsEdition = false,
}: {
  state: StoreState;
  allowSalesAdvisorsEdition?: boolean;
}) => {
  const errors = {
    nameError: !state.name,
    addressError: !isAddressValid(state?.address, true),
    logisticsAddressError: !isAddressEmpty(state?.logisticsAddress)
      ? !isAddressValid(state?.logisticsAddress, false)
      : false,
    phoneError: state?.phone ? !isPhoneValid(state.phone) : true,
    salesAdvisorError: allowSalesAdvisorsEdition
      ? state.salesAdvisors.some((item) => !item)
      : false,
  };

  return {
    ...errors,
    hasError: Object.values(errors).some((errorValue) => !!errorValue),
  };
};
